import { Breadcrumbs } from "@/parts/breadcrumbs";
import { Footer } from "@/parts/footer";
import { Layout } from "@/parts/layout";
import { Meta } from "@/parts/meta";
import { client, getClient } from "@/utils/client";
import type { GetStaticPropsContext } from "next";
import { dodaysQuery, settingsQuery } from "@/utils/queries";
import { IDoDay, ISettings } from "@/utils/types";

export default function DoDay({
  dodays,
  doday,
  settings,
}: {
  settings: ISettings;
  dodays: IDoDay[];
  doday: IDoDay;
}) {
  return (
    <div
      style={{
        backgroundColor: doday.backgroundColor?.value,
        color: doday.accentColor?.value,
      }}
    >
      <Meta
        settings={settings}
        data={{
          title: doday.name ?? "",
          description: doday.description ?? "",
          noIndex: false,
          keywords: "",
          image: doday.thumbnail?.image,
        }}
      />
      <Breadcrumbs
        items={[
          { label: "Home", href: "/" },
          { label: "Doe-dagen", href: "/doe-dagen" },
          { label: doday.name ?? "" },
        ]}
      />
      <Layout
        backgroundColor={doday.backgroundColor?.value}
        accentColor={doday.accentColor?.value}
        data={doday.layout}
        location={doday.location}
        date={doday.date}
        endDate={doday.endDate}
        timeOverride={doday.timeOverride}
        thumbnail={doday.thumbnail}
      />
      <Footer data={settings} />
    </div>
  );
}

export async function getStaticProps(ctx: GetStaticPropsContext) {
  const params = ctx.params as { slug: string };
  const draftMode = ctx.draftMode ?? false;
  const client = getClient(draftMode);
  const query = `*[_type == "doday" && slug.current == $slug]{
        ...,
        layout {
          ...,
          parts[]{
            ...,
            gallery{
              ...,
              items[]{
                ...,
                "playbackId": video.asset->playbackId
              }
            },
            "members": select(
              memberSource == "allTeamMembers" => *[_type == "teamMember"]{..., "videoPlaybackId": video.asset->playbackId},
              memberSource == "allMembers" => *[_type == "member"]{..., "videoPlaybackId": video.asset->playbackId},
              memberSource == "allTestimonials" => *[_type == "testimonial"]{..., "title": name, "quote": coalesce(headline, quote), "description": quote, "role": coalesce(role, company), "partnerLogo": partner->logo, "partnerLogoLight": partner->lightLogo, "videoPlaybackId": video.asset->playbackId},
              members[]->{..., "title": coalesce(title, name), "quote": coalesce(headline, quote), "description": coalesce(description, quote), "role": coalesce(role, company), "partnerLogo": partner->logo, "partnerLogoLight": partner->lightLogo, "videoPlaybackId": video.asset->playbackId}
            ),
            "partners": select(allPartners == true => *[_type == "partner"], partners[]->),
            _type == 'testimonials' => {
              ...,
              "testimonials": testimonials[]->{
                name,
                role,
                company,
                quote,
                image,
                button
              }
            },
            _type == 'stats' => {
              ...,
              "items": select(
                allStats == true => *[_type == "stat"] | order(order asc) {
                  ...,
                  media[]{
                    ...,
                    "playbackId": video.asset->playbackId
                  }
                },
                items[]->{
                  ...,
                  media[]{
                    ...,
                    "playbackId": video.asset->playbackId
                  }
                }
              ),
              "partners": *[_type == "partner"],
            },
            _type == 'knowledge' => {
              ...,
              highlights[]{
                ...,
                "fileURL": file.asset->url
              },
              items[]{
                ...,
                
                items[]{
                  ...,
                  "fileURL": file.asset->url
                }
              }
            },
            _type == 'faqs' => {
              ...,
              "collectionQuestions": *[_type == "faqItem" && count(audience[@ in ^.^.audience]) > 0]{
                question,
                answer
              },
              "pickedQuestions": items[]->{question, answer}
            },
            _type == 'blogPosts' => {
              ...,
              "blogs": *[_type == "blogPost" && ^.tag in tags] | order(date desc)
            }
              
          }
        }
      }`;
  const settings = await client.fetch(settingsQuery);

  const options = { slug: params.slug };

  const doday = await client.fetch(query, options);
  const dodays = await client.fetch(dodaysQuery, options);

  if (!doday[0]?.layout?.parts || doday[0].layout.parts.length === 0) {
    return {
      notFound: true,
    };
  }

  return {
    props: { doday: doday[0], dodays, settings, draftMode },
    revalidate: 5,
  };
}

export async function getStaticPaths() {
  // const query = `*[_type == "doday" && defined(layout.parts) && length(layout.parts) > 0]{ 'slug': slug.current }`;
  const query = `*[_type == "doday"]{ 'slug': slug.current }`;

  const dodays = await client.fetch(query);

  const paths =
    dodays?.map((doday: { slug: any }) => ({
      params: {
        slug: doday.slug,
      },
    })) || [];

  return {
    paths,
    fallback: "blocking",
  };
}
